import {
    inject,
    Injectable
} from '@angular/core';

import {
    HttpClient
} from '@angular/common/http';

import {
    ConfigurationManagerService,
    ConfigurationPreferences,
    EntityManagerService,
    EnvironmentInfoService,
    UserActiveSessionManagerService
} from 'src/app/core';

import { stringToLines } from 'src/app/shared/string-utils';
import { MathUtils } from 'src/app/shared/utils';

import {
    ChainCampaigns,
    FidelitiesPoints,
    Sales,
    SalesCustomer
} from 'tilby-models';

import {
    ActiveSaleService
} from 'src/app/features';

import {
    TranslateService
} from '@ngx-translate/core';

import {
    documentPrinter
} from 'app/ajs-upgraded-providers';

import {
    TilbyCurrencyPipe
} from '@tilby/tilby-ui-lib/pipes/tilby-currency';

import {
    firstValueFrom
} from 'rxjs';

type CustomerFidelityInfo = {
    customer_uuid: string;
    total_credit: number;
    loyalty_points: number;
}

type CustomerDisplayPayload = {
    customer: {
        prepaid_credit?: number;
        loyalty_points?: number;
    };
    sale: Sales | null;
    settings: Partial<ConfigurationPreferences>;
    shop: string;
    strings: CustomerDisplayStrings;
    qrcode?: {
        url: string,
        text: string
    };
}

type CustomerDisplayStrings = {
    total: string;
    credit_balance: string;
    points_balance: string;
    title_box_qr_code: string;
}

@Injectable({
    providedIn: 'root'
})
export class CustomerDisplayManagerService {
    private readonly activeSale = inject(ActiveSaleService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly documentPrinter = inject(documentPrinter);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly httpClient = inject(HttpClient);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly translateService = inject(TranslateService);
    private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);

    private customerFidelityInfo: CustomerFidelityInfo | null = null;

    /**
     * Generates a sale item display row based on the given parameters.
     *
     * @param {number} displayRowSize - The size of the display row.
     * @param {string} itemName - The name of the item.
     * @param {number} itemPrice - The price of the item.
     * @returns {string} - The generated sale item display row.
     */
    private getSaleItemDispRow(displayRowSize: number, itemName: string, itemPrice: number): string {
        const priceString = this.tilbyCurrencyPipe.transform(itemPrice, '');
        const itemNameString = (itemName || '')
            .slice(0, displayRowSize - (priceString.length + 1))
            .padEnd(displayRowSize - priceString.length);

        return itemNameString + priceString;
    }

    /**
     * Generates the display row for the subtotal with a given display row size.
     *
     * @param {number} displayRowSize - The size of the display row.
     * @param {Object} options - (optional) Additional options for generating the subtotal display row.
     * @param {boolean} options.useAbsValue - (optional) Specifies whether to use the absolute value of the final amount.
     * @param {string} options.subtotalLabel - (optional) The label to be used for the subtotal. If not provided, the default label will be used.
     * @return {string} The generated subtotal display row.
     */
    private getSubTotalDispRow(displayRowSize: number, options?: { useAbsValue: boolean, subtotalLabel?: string }): string {
        const finalAmountString = this.tilbyCurrencyPipe.transform(options?.useAbsValue ? Math.abs(this.activeSale.currentSale.final_amount!) : this.activeSale.currentSale.final_amount, '');

        const subTotalLabelString = `${options?.subtotalLabel || this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.SUBTOTAL')}`
            .slice(0, displayRowSize - (finalAmountString.length + 1))
            .padEnd(displayRowSize - finalAmountString.length);

        return subTotalLabelString + finalAmountString;
    }

    /**
     * Updates the customer fidelity internal cache.
     *
     * @param {SalesCustomer} saleCustomer - The sales customer object.
     * @return {Promise<void>} A promise that resolves when the customer fidelity information is updated.
     */
    private async updateCustomerFidelityInfo(saleCustomer?: SalesCustomer) {
        if (saleCustomer?.uuid === this.customerFidelityInfo?.customer_uuid) {
            return;
        }

        const customerFidelity = saleCustomer?.fidelity;

        if (!customerFidelity) {
            this.customerFidelityInfo = null;
            return;
        }

        try {
            let customerPoints;

            const prepaidCredit = await this.activeSale.getCustomerPrepaidInfo();
            const campaigns = await this.entityManagerService.campaigns.fetchCollectionOnline();

            if (Array.isArray(campaigns)) {
                const activeCampaign = campaigns.find((campaign: ChainCampaigns) => campaign.isValid);

                if (activeCampaign) {
                    const fidelitiesPoints = await this.entityManagerService.fidelitiesPoints.fetchCollectionOffline({ fidelity: customerFidelity, campaign_id: activeCampaign.id });
                    customerPoints = fidelitiesPoints.find((fidelityPoint: FidelitiesPoints) => fidelityPoint.campaign_id === activeCampaign.id);
                }
            }

            this.customerFidelityInfo = {
                customer_uuid: saleCustomer?.uuid!,
                total_credit: MathUtils.round(prepaidCredit?.total_credit || 0),
                loyalty_points: MathUtils.round(customerPoints?.points || 0)
            };
        } catch (err) { }
    };

    /**
     * Updates the display with two rows of information based on the given event type and data.
     *
     * @param {string} eventType - The type of event triggering the display update.
     * @param {any} data - The data associated with the event.
     */
    public async updateTwoRowsDisplay(eventData: { event: string, data?: any }) {
        const { event: eventType, data } = eventData;
        const displayRowSize = parseInt(this.configurationManagerService.getPreference('customer_display.display_row_size') || '') || 20;
        const printer = this.activeSale.printerDocumentData?.printer;
        const canUsePrinter = !!(printer && this.documentPrinter.isPrinterUsable(printer));
        const serialDisplayPort = this.environmentInfoService.canUseSerialPorts() ? this.configurationManagerService.getPreference('customer_display.serial_port_address') : null;

        if (!canUsePrinter && !serialDisplayPort) {
            return;
        }

        const rowsToDisplay: string[] = [];

        switch (eventType) {
            case 'customer-added':
                const welcomeText = this.configurationManagerService.getPreference('customer_display.greeting_message') || this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.DEFAULT_GREETING');
                let fidelityRow = "";

                await this.updateCustomerFidelityInfo(data);

                if (this.customerFidelityInfo?.loyalty_points) {
                    fidelityRow += `${this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.POINTS_BALANCE_TWO_ROWS')}: ${this.customerFidelityInfo?.loyalty_points}`;
                }

                if (this.customerFidelityInfo?.total_credit) {
                    if (fidelityRow) {
                        fidelityRow += " - ";
                    }

                    fidelityRow += `${this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.CREDIT_BALANCE_TWO_ROWS')}: ${this.customerFidelityInfo?.total_credit}`;
                }

                rowsToDisplay.push(
                    (`${welcomeText} ${data.first_name}`).padEnd(displayRowSize),
                    fidelityRow.padEnd(displayRowSize),
                );
                break;
            case 'item-added':
                rowsToDisplay.push(
                    this.getSaleItemDispRow(displayRowSize, data.name, data.price),
                    this.getSubTotalDispRow(displayRowSize)
                );
                break;
            case 'item-changed':
                let name = data.currentSaleItem.name;
                let price = (data.currentSaleItem.quantity >= data.previousSaleItem.quantity) ? data.currentSaleItem.price : -data.currentSaleItem.price;

                rowsToDisplay.push(
                    this.getSaleItemDispRow(displayRowSize, name, price),
                    this.getSubTotalDispRow(displayRowSize)
                );
                break;
            case 'item-removed':
                rowsToDisplay.push(
                    this.getSaleItemDispRow(displayRowSize, data.name, -data.price),
                    this.getSubTotalDispRow(displayRowSize)
                );
                break;
            case 'priceChange-added':
                rowsToDisplay.push(
                    this.getSaleItemDispRow(displayRowSize, data.priceChange.description, data.priceChange.amount),
                    this.getSubTotalDispRow(displayRowSize)
                );
                break;
            case 'priceChange-removed':
                rowsToDisplay.push(
                    this.getSaleItemDispRow(displayRowSize, data.priceChange.description, -data.priceChange.amount),
                    this.getSubTotalDispRow(displayRowSize)
                );
                break;
            case 'go-to-payments':
                let subtotalLabel = this.activeSale.currentSale.final_amount! > 0 ? this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.TO_PAY') : this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.REFUND');

                rowsToDisplay.push(
                    (' ').repeat(displayRowSize),
                    this.getSubTotalDispRow(displayRowSize, { subtotalLabel: subtotalLabel, useAbsValue: true })
                );
                break;
            case 'sale-opened':
                if (!data) {
                    let thankYouMessage = this.configurationManagerService.getPreference('customer_display.thankyou_message');

                    if (thankYouMessage) {
                        let thankYouRows = stringToLines(thankYouMessage, displayRowSize);

                        for (let i = 0; i < 2; i++) {
                            rowsToDisplay.push((thankYouRows[i] || '').padEnd(displayRowSize));
                        }
                    } else {
                        rowsToDisplay.push(
                            this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.DEFAULT_THANK_YOU_FIRST_ROW').padEnd(displayRowSize),
                            this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.DEFAULT_THANK_YOU_SECOND_ROW').padEnd(displayRowSize)
                        );
                    }
                }
                break;
            default:
                break;
        }

        if (!rowsToDisplay.length) {
            return;
        }

        //Send to the current printer (if possible)
        if (canUsePrinter) {
            this.documentPrinter.displayText(printer, rowsToDisplay);
        }

        //Send to the display serial port (if possible)
        if (serialDisplayPort) {
            this.documentPrinter.displayText({
                name: '',
                connection_type: 'sp',
                driver: 'escpos',
                ip_address: serialDisplayPort,
                type: 'receipt'
            }, rowsToDisplay);
        }
    }

    /**
     * Returns a payload for the customer display.
     *
     * @param {Sales | null} sale - The sale object to include in the payload. Defaults to null.
     * @param {CustomerFidelityInfo | null} customerFidelityInfo - The customer fidelity information to include in the payload. Defaults to null.
     * @return {CustomerDisplayPayload} The payload object containing the sale, settings, customer info and strings.
     */
    private getCustomerDisplayPayload(sale?: Sales | null, customerFidelityInfo?: CustomerFidelityInfo | null): CustomerDisplayPayload {
        return {
            sale: sale || null,
            shop: this.userActiveSessionManagerService.getSession()?.shop.name!,
            settings: this.configurationManagerService.getShopPreferences('customer_display.'),
            customer: {
                prepaid_credit: customerFidelityInfo?.total_credit,
                loyalty_points: customerFidelityInfo?.loyalty_points
            },
            strings: {
                total: this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.TOTAL'),
                credit_balance: this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.CREDIT_BALANCE'),
                points_balance: this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.POINTS_BALANCE'),
                title_box_qr_code: this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.TITLE_BOX_QR_CODE'),
            }
        }
    }

    /**
     * Updates the color display for a given sale.
     *
     * @param {Sales} sale - the sale object to update the color display for
     * @return {Promise<void>} a promise that resolves when the color display is updated
     */
    public async updateColorDisplay(sale?: Sales, options?: { overrides?: Partial<CustomerDisplayPayload>, stringsOverrides?: Partial<CustomerDisplayStrings> }) {
        if(!sale) {
            sale = this.activeSale.currentSale;
        }

        // Get the uuid of the sale
        const saleUuid = sale.uuid;

        await this.updateCustomerFidelityInfo(sale.sale_customer);

        const payload = this.getCustomerDisplayPayload(sale, this.customerFidelityInfo);

        if (this.configurationManagerService.getPreference('cashregister.public_receipt') && saleUuid) {
            payload.qrcode = {
                url: this.environmentInfoService.getPdfUrl(saleUuid) || '',
                text: this.translateService.instant('CASHREGISTER.CUSTOMER_DISPLAY.SCAN_SALE_QR_CODE')
            };
        }

        if(options?.overrides) {
            Object.assign(payload, options?.overrides);
        }

        if(options?.stringsOverrides) {
            Object.assign(payload.strings, options?.stringsOverrides);
        }

        if (this.isColorDisplayEnabled()) {
            window.require('electron').ipcRenderer.send('update-customer-display', payload);
        }

        const netDisplayIP = this.getNetworkDisplayIP();

        if(netDisplayIP) {
            this.sendNetworkDisplayMessage(netDisplayIP, payload);
        }
    }

    /**
     * Returns true if the color display is enabled, false otherwise.
     *
     * The color display is enabled if the application is running in an Electron
     * environment and the preference 'customer_display.enable_color_display' is true.
     *
     * @return {boolean} Whether the color display is enabled
     */
    public isColorDisplayEnabled() {
        return this.environmentInfoService.isElectronApp() && !!this.configurationManagerService.getPreference('customer_display.enable_color_display');
    }
    
    /**
     * Returns the IP address of the network display, if the network display is enabled.
     * If the network display is not enabled, returns null.
     * The IP address is determined in the following order of priority:
     * 1. If the local storage contains a value for the key 'deviceSettings::NetworkDisplayAddress', returns that value.
     * 2. If the shop configuration contains a value for 'customer_display.network_display_ip_address', returns that value.
     * 3. If neither of the above is true, returns null.
     * @return {string | null} The IP address of the network display, or null if the network display is not enabled
     */
    public getNetworkDisplayIP() {
        return this.configurationManagerService.getPreference('customer_display.enable_network_display')
            ? window.localStorage.getItem('deviceSettings::NetworkDisplayAddress') || this.configurationManagerService.getPreference('customer_display.network_display_ip_address') || null
            : null;
    }

    /**
     * Configure a network display to override the display saved in the shop configuration.
     * @param ipAddress The IP address of the network display
     * @param version The version of the network display
     * @returns {Promise<void>} A promise that resolves when the network display is configured
     */
    public async configureNetworkDisplay(ipAddress: string, version: string) {
        if (!ipAddress) {
            return;
        }

        // Create a test payload
        const payload = this.getCustomerDisplayPayload(null, null);

        // Send the test payload
        await this.sendNetworkDisplayMessage(ipAddress, payload);

        // Save the IP address to the local storage (overrides the shop configuration if it exists)
        window.localStorage.setItem('deviceSettings::NetworkDisplayAddress', ipAddress);
    }

    /**
     * Sends a network display message to the given IP address.
     * @param ipAddress The IP address of the network display
     * @param payload The payload to send
     * @returns {Promise} A promise that resolves when the network display is sent
     */
    public async sendNetworkDisplayMessage(ipAddress: string, payload: CustomerDisplayPayload) {
        if (!ipAddress) {
            return;
        }

        let port = 2013;

        // If the IP address includes a port, split it and use the port from the string
        if (ipAddress.includes(':')) {
            const split = ipAddress.split(':');
            ipAddress = split[0];
            port = parseInt(split[1]);
        }

        return firstValueFrom(this.httpClient.post(`http://${ipAddress}:${port}/screen_update`, payload));
    }
}